import React, { useState, useEffect } from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import "../../styles/common/popup.scss";
import { getInitialTests } from "../../helper/apicalls/initialTest";
import { getAllIQTest } from "../../helper/apicalls/iqTest";
import GlobalLoader from "./GlobalLoader";
import Cookies from "js-cookie";
import { updateCourse } from "../../helper/apicalls/course";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

const CoursePublishPopup = ({
  open,
  courseData,
  onClose,
  onConfirm,
  setShowSuccessPopup,
  title = "Publish Course",
}) => {
  const auth_token = Cookies.get("admin_token");
  const [psychometricTest, setPsychometricTest] = useState("");
  const [iqTest, setIqTest] = useState("");
  const [psychometricOptions, setPsychometricOptions] = useState([]);
  const [iqTestOptions, setIqTestOptions] = useState([]);
  const [loader, setloader] = useState(false);
  const [error, setError] = useState("");

  const SelectIcon = ({ value, onClear }) => {
    if (value) {
      return (
        <CloseIcon
          sx={{
            cursor: "pointer",
            fontSize: 20,
            color: "#6b7280",
            mr: 1,
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            onClear();
          }}
        />
      );
    }

    return <ArrowDropDownIcon />;
  };

  const fetchInitialTests = async () => {
    setloader(true);
    try {
      const res = await getInitialTests();
      setPsychometricOptions(res || []);
    } catch (err) {
      console.error(err);
      setPsychometricOptions([]);
    } finally {
      setloader(false);
    }
  };

  const fetchIqTestData = async () => {
    setloader(true);
    try {
      const res = await getAllIQTest(auth_token, "iq");
      setIqTestOptions(res || []);
    } catch (err) {
      console.error(err);
      setIqTestOptions([]);
    } finally {
      setloader(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!psychometricTest && !iqTest) {
      setError("Please select at least one test.");
      return;
    }
    setError("");

    try {
      const updatedCourseData = {};
      if (psychometricTest) {
        updatedCourseData.psychometricTest = psychometricTest;
      }
      if (iqTest) {
        updatedCourseData.iqTest = iqTest;
      }
      if (psychometricTest && iqTest) {
        updatedCourseData.psychometricTest = psychometricTest;
        updatedCourseData.iqTest = iqTest;
      }
      updatedCourseData.publish = true;
      updatedCourseData.key = courseData.key;

      const res = await updateCourse(updatedCourseData);
      if (onConfirm) {
        onConfirm({ psychometricTest, iqTest });
      }
      if (res) {
        setShowSuccessPopup(true);
      }
    } catch (error) {
      console.error("Failed to update course:", error);
      setError("Failed to update course. Please try again.");
    }
  };

  useEffect(() => {
    if (open) {
      fetchInitialTests();
      fetchIqTestData();
      setPsychometricTest("");
      setIqTest("");
      setError("");
      setPsychometricOptions([]);
      setIqTestOptions([]);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="common-popup-overlay" onClick={onClose}>
      {loader && <GlobalLoader visible={loader} />}
      <div
        className="common-popup-container"
        style={{ padding: "16px", borderRadius: "8px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header with-close">
          <h2>{title}</h2>
          <button
            type="button"
            className="popup-close-button"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="popup-content">
          <form onSubmit={handleSubmit}>
            <div className="popup-form-group">
              <InputLabel shrink htmlFor="psychometric-select">
                Psychometric Test
              </InputLabel>
              <FormControl fullWidth>
                <Select
                  id="psychometric-select"
                  value={psychometricTest}
                  onChange={(e) => {
                    setPsychometricTest(e.target.value);
                    setError("");
                  }}
                  IconComponent={() => (
                    <SelectIcon
                      value={psychometricTest}
                      onClear={() => setPsychometricTest("")}
                    />
                  )}
                  displayEmpty
                  renderValue={
                    psychometricTest !== ""
                      ? undefined
                      : () => (
                          <span style={{ color: "#9ca3af" }}>
                            Select a psychometric test
                          </span>
                        )
                  }
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "10px 40px 10px 16px",
                      borderRadius: "8px",
                      border: "1px solid var(--secondary-border)",
                      background: "var(--color-bg)",
                    },
                    "& fieldset": {
                      border: "1px solid var(--secondary-border)",
                    },
                  }}
                >
                  <MenuItem disabled value="">
                    <em>Select a psychometric test</em>
                  </MenuItem>
                  {psychometricOptions.map((opt) => (
                    <MenuItem
                      key={opt.value || opt.id}
                      value={opt.value || opt.id}
                    >
                      {opt.label || opt.name || opt.value || opt.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="popup-form-group">
              <InputLabel shrink htmlFor="iq-select">
                IQ Test
              </InputLabel>
              <FormControl fullWidth>
                <Select
                  id="iq-select"
                  value={iqTest}
                  onChange={(e) => {
                    setIqTest(e.target.value);
                    setError("");
                  }}
                  IconComponent={() => (
                    <SelectIcon value={iqTest} onClear={() => setIqTest("")} />
                  )}
                  displayEmpty
                  renderValue={
                    iqTest !== ""
                      ? undefined
                      : () => (
                          <span style={{ color: "#9ca3af" }}>
                            Select an IQ test
                          </span>
                        )
                  }
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "10px 40px 10px 16px",
                      borderRadius: "8px",
                      border: "1px solid var(--secondary-border)",
                      background: "var(--color-bg)",
                    },
                    "& fieldset": {
                      border: "1px solid var(--secondary-border)",
                    },
                  }}
                >
                  <MenuItem disabled value="">
                    <em>Select an IQ test</em>
                  </MenuItem>
                  {iqTestOptions.map((opt) => (
                    <MenuItem
                      key={opt.value || opt.id}
                      value={opt.value || opt.id}
                    >
                      {opt.label || opt.name || opt.value || opt.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="popup-note">
              <p>Note: You can select either one or both tests.</p>
            </div>

            {error && <div className="popup-error-message">{error}</div>}

            <div className="popup-actions">
              <button
                type="submit"
                className="popup-submit-button"
                disabled={!psychometricTest && !iqTest}
              >
                Publish
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CoursePublishPopup;
